// ─── AutoChinBot · etask level store ───────────────────────────
// Persists the "task2" global min/max levels (min/max close of the last
// 50 / 100 / 200 completed candles) to the `autochin_etask_levels` table so
// they survive reloads / device switches and can be reused by other tasks.
//
// Table shape (verified):
//   PK (user_id, symbol_id) — RLS restricted to the owning user.
//   Columns: user_id, symbol_id, symbol_name,
//            min50, max50, min100, max100, min200, max200,
//            candle_timestamp, updated_at

import { supabase } from '@/lib/supabase';

export interface AutoChinMinMaxLevels {
  min50: number | null;
  max50: number | null;
  min100: number | null;
  max100: number | null;
  min200: number | null;
  max200: number | null;
  /** Timestamp of A0 when these levels were last computed. */
  candleTimestamp: number | null;
  updatedAt: number;
}

export interface EtaskLevelRow {
  symbolId: number;
  symbolName: string;
  levels: AutoChinMinMaxLevels;
}

/**
 * Upsert the current levels for the given symbols (batched by the engine).
 * Best-effort: failures are logged, never thrown.
 */
export async function persistEtaskLevels(userId: string, rows: EtaskLevelRow[]): Promise<void> {
  if (!userId || rows.length === 0) return;
  const nowIso = new Date().toISOString();

  const payload = rows.map((r) => ({
    user_id: userId,
    symbol_id: r.symbolId,
    symbol_name: r.symbolName,
    min50: r.levels.min50,
    max50: r.levels.max50,
    min100: r.levels.min100,
    max100: r.levels.max100,
    min200: r.levels.min200,
    max200: r.levels.max200,
    candle_timestamp: r.levels.candleTimestamp,
    updated_at: nowIso,
  }));

  try {
    const { error } = await supabase
      .from('autochin_etask_levels')
      .upsert(payload, { onConflict: 'user_id,symbol_id' });
    if (error) console.warn('[AutoChinEtask] persist levels error:', error.message);
  } catch (err) {
    console.warn('[AutoChinEtask] persist levels failed:', err);
  }
}